import { useState } from 'react';

interface ModelData {
  id: string;
  name: string;
  area: string;
  createTime: string;
  updateTime: string;
  accuracy: string;
  size: string;
}

interface DrillRecord {
  id: string;
  scenario: string;
  participants: number;
  duration: string;
  date: string;
  result: string;
}

export default function ThreeDModeling() {
  const [models] = useState<ModelData[]>([
    { id: 'M001', name: '港区全景模型', area: '整个港区', createTime: '2025-01-10', updateTime: '2025-01-15', accuracy: '厘米级', size: '2.8GB' },
    { id: 'M002', name: '危险品仓库区', area: 'A区仓库群', createTime: '2025-01-12', updateTime: '2025-01-15', accuracy: '厘米级', size: '850MB' },
    { id: 'M003', name: '码头作业区', area: '1-5号码头', createTime: '2025-01-08', updateTime: '2025-01-14', accuracy: '厘米级', size: '1.5GB' },
  ]);

  const [drills] = useState<DrillRecord[]>([
    { id: 'D001', scenario: '危险品泄漏应急', participants: 45, duration: '2小时', date: '2025-01-10', result: '优秀' },
    { id: 'D002', scenario: '火灾扑救演练', participants: 38, duration: '1.5小时', date: '2025-01-12', result: '良好' },
    { id: 'D003', scenario: '人员疏散演练', participants: 120, duration: '1小时', date: '2025-01-14', result: '优秀' },
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">三维建模与应急推演</h2>
        <div className="flex gap-3">
          <button className="px-4 py-2 bg-slate-700 text-white text-sm rounded-lg hover:bg-slate-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-refresh-line mr-2"></i>更新模型
          </button>
          <button className="px-4 py-2 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-play-line mr-2"></i>启动推演
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">三维模型</span>
            <i className="ri-3d-box-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">{models.length}</div>
          <div className="text-xs text-slate-500 mt-1">个</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">推演次数</span>
            <i className="ri-play-circle-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">{drills.length}</div>
          <div className="text-xs text-slate-500 mt-1">次</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">参与人数</span>
            <i className="ri-team-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">203</div>
          <div className="text-xs text-slate-500 mt-1">人次</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">模型精度</span>
            <i className="ri-focus-3-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-teal-400">厘米级</div>
          <div className="text-xs text-slate-500 mt-1">高精度</div>
        </div>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">数字沙盘 - 港区三维实景</h3>
        <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative">
          <img 
            src="https://public.readdy.ai/ai/img_res/06cb1d1be029c45145a590cf0d285c8f.jpg"
            alt="三维模型"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute top-4 left-4 bg-teal-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
            <i className="ri-3d-box-line mr-2"></i>
            三维模型
          </div>
          <div className="absolute top-4 right-4 flex gap-2">
            <button className="bg-slate-900/80 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-xs hover:bg-slate-800 transition-colors cursor-pointer">
              <i className="ri-zoom-in-line mr-1"></i>放大
            </button>
            <button className="bg-slate-900/80 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-xs hover:bg-slate-800 transition-colors cursor-pointer">
              <i className="ri-rotate-lock-line mr-1"></i>旋转
            </button>
            <button className="bg-slate-900/80 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-xs hover:bg-slate-800 transition-colors cursor-pointer">
              <i className="ri-fullscreen-line mr-1"></i>全屏
            </button>
          </div>
          <div className="absolute bottom-4 left-4 right-4 bg-slate-900/80 backdrop-blur-sm rounded-lg p-4">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-slate-400">模型名称:</span>
                <span className="text-white ml-2">港区全景模型</span>
              </div>
              <div>
                <span className="text-slate-400">覆盖面积:</span>
                <span className="text-white ml-2">5.2 km²</span>
              </div>
              <div>
                <span className="text-slate-400">精度:</span>
                <span className="text-teal-400 ml-2">厘米级</span>
              </div>
              <div>
                <span className="text-slate-400">更新时间:</span>
                <span className="text-white ml-2">2025-01-15</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white">三维模型列表</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">模型名称</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">覆盖区域</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">精度</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">更新时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {models.map((model) => (
                  <tr key={model.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-white">{model.name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{model.area}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-teal-400">{model.accuracy}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{model.updateTime}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <button className="text-teal-400 hover:text-teal-300 mr-3 cursor-pointer">
                        <i className="ri-eye-line"></i>
                      </button>
                      <button className="text-slate-400 hover:text-slate-300 cursor-pointer">
                        <i className="ri-download-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white">应急推演记录</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">推演场景</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">参与人数</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">时长</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">日期</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">评价</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {drills.map((drill) => (
                  <tr key={drill.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3 text-sm text-slate-300">{drill.scenario}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{drill.participants}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{drill.duration}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{drill.date}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        drill.result === '优秀' ? 'bg-teal-500/20 text-teal-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {drill.result}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
